import { getPageParam, usePagination } from '@/common/hooks/usePagination';
import { useScrollTopOnChange } from '@/common/hooks/useScrollTopOnChange';
import { useSort } from '@/common/hooks/useSort';
import { AllSortMethods } from '@/common/utils/AllSortMethods';
import { useAppContext } from '@/context/app';
import mergeOperations from '@/util/shallowMerge';
import { useLazyQuery } from '@apollo/client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useQueryState } from 'nuqs';
import { useCallback, useEffect, useState } from 'react';
import { useAddJslog } from '../App/useAddJslog';
import { pushEcommerceEvent } from '@/lib/data-layer';
import DEFAULT_OPERATIONS from './category.gql';
export const useStickyHeader = (ref: React.RefObject<HTMLElement>) => {
  const [
    { isFilterSticky },
    {
      actions: { setFilterSticky },
    },
  ] = useAppContext();
  const [elementBottom, setElementBottom] = useState(0);
  const [headerBottom, setHeaderBottom] = useState(0);
  useEffect(() => {
    if (!ref.current) return;

    const handleScroll = () => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      // 当元素顶部接近视口顶部时固定
      if (rect.top <= 0 && !isFilterSticky) {
        setFilterSticky(true);
      }
      // 滚动距离小于顶部菜单栏底部时取消固定

      if (scrollTop <= headerBottom && isFilterSticky) {
        setFilterSticky(false);
      }
      // 当滚动到元素原始底部位置时取消固定
      if (scrollTop <= elementBottom - rect.height && isFilterSticky) {
        setFilterSticky(false);
      }
    };

    // 初始化元素底部位置
    if (elementBottom === 0) {
      const headerEl = document.getElementById('headerBox');
      const headerRect = headerEl?.getBoundingClientRect();
      const bottomDistance = (headerRect?.top ?? 0) + (headerEl?.offsetHeight ?? 0);
      console.log('bottomDistance', bottomDistance);
      setHeaderBottom(bottomDistance);
      // console.log('setElementBottom1', ref.current.offsetTop, ref.current.offsetHeight);
      if (ref.current.offsetHeight < 50) {
        setFilterSticky(false);
        setTimeout(() => {
          console.log('setElementBottom2', ref.current.offsetTop, ref.current.offsetHeight);
          setElementBottom(ref.current.offsetTop + ref.current.offsetHeight);
        }, 100);
      } else {
        setElementBottom(ref.current.offsetTop + ref.current.offsetHeight);
      }
    }
    if (elementBottom === 0) {
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isFilterSticky, elementBottom, ref]);

  return isFilterSticky;
};

export const useCategory = ({ id, filters, pageSize }: any) => {
  const { addLog } = useAddJslog();

  const operations = mergeOperations(DEFAULT_OPERATIONS);
  const { getCategoryQuery } = operations;

  const router = useRouter();

  const [paginationValues, paginationApi] = usePagination();

  const { currentPage, totalPages } = paginationValues;

  const { setCurrentPage, setTotalPages } = paginationApi;

  const [categoryData, setCategoryData] = useState(null);

  const pathname = usePathname();

  const searchParams = useSearchParams();

  const [sortState, sortApi] = useSort();

  const onError = useCallback(
    (err: any) => {
      try {
        addLog({
          variables: {
            type: 'paypal',
            message: err.toString(),
            time: new Date().toString(),
            remark1: 'hisea category',
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [addLog],
  );

  const setPage = useCallback(
    (page: any) => {
      setCurrentPage(page);
      let destination;

      const pathArr = pathname.split('/').filter(temp => !!temp);
      const findIndex = pathArr.findIndex(temp => temp === 'p');

      if (page === 1) {
        if (findIndex > -1 && /^\d+$/.test(pathArr[findIndex + 1])) {
          pathArr.splice(findIndex, 2);
        }
      } else {
        if (findIndex > -1 && /^\d+$/.test(pathArr[findIndex + 1])) {
          pathArr[findIndex + 1] = page;
        } else {
          pathArr.push(...['p', page]);
        }
      }

      destination = pathArr.join('/');

      if (destination[0] !== '/') {
        destination = '/' + destination;
      }

      if (destination[destination.length - 1] !== '/') {
        destination += '/';
      }

      if (searchParams.size) {
        destination += `?${searchParams.toString()}`;
      }

      router.push(destination);
    },
    [pathname, router, searchParams, setCurrentPage],
  );

  const pageControl = {
    currentPage,
    setPage,
    totalPages,
  };

  const [runQuery, { called: categoryCalled, loading: categoryLoading, error, data: categoriesData, fetchMore }] =
    useLazyQuery(getCategoryQuery, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    });

  const [sort_by, setSortBy] = useQueryState('sort_by', {
    defaultValue: '',
  });

  // 加上接口错误日志
  useEffect(() => {
    if (error) {
      try {
        addLog({
          variables: {
            type: 'category_page_error',
            message: error.toString(),
            time: new Date().toString(),
            remark1: 'hisea category 接口错误',
          },
        });
      } catch (error) {
        console.error(error);
      }
    }
  }, [error]);

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(location.search);

      setCurrentPage(getPageParam(searchParams, location));

      const sortMethods = AllSortMethods.find(temp => temp.sortQuery === sort_by);
      const sort = sortMethods ? sortMethods.sortAttribute : sortState.sortAttribute;

      console.log('run query...............');

      runQuery({
        variables: {
          currentPage: getPageParam(searchParams, location),
          id: id,
          filters,
          pageSize,
          sort: {
            [sort]: sort === 'lw_high_to_low' ? 'DESC' : sortState.sortDirection,
          },
        },
        fetchPolicy: 'cache-and-network',
      });
    } catch (error) {
      console.error(error);
    }
  }, [pathname, sort_by]);

  const totalPagesFromData =
    categoriesData && categoriesData.products ? categoriesData.products.page_info.total_pages : null;

  useEffect(() => {
    if (totalPagesFromData) {
      setTotalPages(totalPagesFromData);
    }

    return () => {
      setTotalPages(null);
    };
  }, [setTotalPages, totalPagesFromData]);

  useEffect(() => {
    if (!categoryLoading && categoriesData) {
      setCategoryData({
        categories: categoriesData.categories,
        products: categoriesData.products,
      });
    } else if (!categoryLoading && !categoriesData) {
      // setPage(1)
    }
  }, [categoriesData, categoryLoading, setPage, sort_by]);

  const categoryNotFound = !categoryLoading && categoriesData?.categories?.items.length === 0;

  const noProducts = !categoryLoading && categoriesData?.products?.items?.length === 0;

  if (!categoryLoading && categoriesData) {
    try {
      const { products, categories } = categoriesData;
      const productItems = products.items.map(temp => {
        return {
          item_name: temp.name,
          item_id: temp.sku,
          quantity: 1,
        };
      });

      const productItemsForPintrk = products.items.map(temp => {
        return {
          product_name: temp.name,
          product_id: temp.uid,
          product_category: categories.items[0].meta_title,
          product_price: temp.price_range.maximum_price.final_price.value,
          product_brand: 'hisea',
        };
      });

      pushEcommerceEvent({
          event: 'view_item_list',
          ecommerce: {
            item_list_id: categories.items[0].name,
            item_list_name: categories.items[0].name,
            items: productItems,
          },
      });


      window.pintrk &&
        window.pintrk('track', 'viewcategory', {
          line_items: productItemsForPintrk,
        });
    } catch (err) {
      console.error(err);
    }
  }

  useScrollTopOnChange(currentPage);

  const loadMore = useCallback(async () => {
    try {
      const { data: categoriesData } = await fetchMore({
        variables: {
          currentPage: currentPage + 1,
          id: id,
          filters,
          pageSize,
          sort: {
            [sortState.sortAttribute]: sortState.sortAttribute === 'lw_high_to_low' ? 'DESC' : sortState.sortDirection,
          },
        },
      });

      setCategoryData({
        categories: categoriesData.categories,
        products: categoriesData.products,
      });

      return result;
    } catch (error) {
      onError(error);

      return null;
    }
  }, [fetchMore, currentPage, id, filters, pageSize, sortState, onError]);

  return {
    categoryData,
    pageControl,
    sortState,
    sortApi,
    categoryNotFound,
    categoryLoading,
    noProducts,
    onError,
    loadMore,
  };
};
